import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { ArtifactService } from '../../../../core/artifact/artifact.service';

import { NavBaseComponent } from './nav-base';
import { persistStepCapture, uploadCapture } from './nav-capture-storage';


/**
 * Full-screen camera interface with damage annotation.
 *
 * Flow:
 *  1. Crew taps "Take Photo" → full-screen overlay opens with live camera preview
 *     via getUserMedia. Falls back to <input capture> if API unavailable.
 *     Or taps "Upload from Phone" → the phone's photo picker (no `capture`, so the
 *     gallery opens, not the camera). Any photo is accepted, no age check (#103);
 *     it goes through the same compress + upload path as a taken one.
 *  2. Crew taps the shutter button → photo is captured from the video stream,
 *     compressed to max 1920px / JPEG 80%, stored locally.
 *  3. Gallery of thumbnails is shown below. Each thumbnail can be removed.
 *  4. If enableDamageAnnotation=true, a panel appears after taking a photo to
 *     mark pre-existing damage with preset reasons + custom text.
 *  5. When required=true, signals readiness to the step gate via onReady callback.
 */
@Component({
    selector: 'app-nav-photo-capture',
    standalone: true,
    imports: [CommonModule, FormsModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        /* ── Card ── */
        .photo-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07); overflow: hidden;
        }
        .photo-header { padding: 0.9rem 1.1rem 0; }
        .photo-title { font-size: 1rem; font-weight: 700; color: #1a1a1a; }
        .photo-subtitle { font-size: 0.85rem; color: #666; margin-top: 0.2rem; }

        /* ── Gallery ── */
        .photo-area { margin: 0.75rem 1.1rem; }
        .thumbnails { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.75rem; }
        .thumb-wrap { position: relative; width: 80px; height: 80px; flex-shrink: 0; }
        .thumb-wrap img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; }
        .thumb-remove {
            position: absolute; top: -6px; right: -6px;
            width: 22px; height: 22px; border-radius: 50%;
            background: #dc2626; color: white; border: none;
            font-size: 0.75rem; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
        }
        .thumb-wrap img.not-stored { opacity: 0.55; }
        .upload-chip {
            position: absolute; top: 2px; left: 2px;
            width: 18px; height: 18px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 0.65rem; font-weight: 800; border: none; padding: 0;
        }
        .upload-chip.uploading { background: rgba(0,0,0,0.55); color: white; }
        .upload-chip.failed { background: #dc2626; color: white; cursor: pointer; }
        .upload-warning {
            display: flex; align-items: flex-start; gap: 0.5rem;
            margin: 0.6rem 1.1rem 0; padding: 0.6rem 0.8rem;
            background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
            font-size: 0.78rem; color: #991b1b; line-height: 1.45;
        }

        .damage-badge {
            position: absolute; bottom: 2px; left: 2px;
            background: rgba(220,38,38,0.85); color: white;
            font-size: 0.6rem; padding: 0.1rem 0.35rem; border-radius: 3px;
        }

        /* ── Capture button ── */
        .capture-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.6rem;
            width: 100%; padding: 1rem; background: #1a1a1a; color: white;
            border: none; border-radius: 8px; font-size: 1rem; font-weight: 600;
            cursor: pointer; margin-bottom: 0.5rem; font-family: inherit;
        }
        .capture-btn:active { background: #000; }
        .capture-btn.secondary { background: white; color: #1a1a1a; border: 1.5px solid #1a1a1a; }
        .capture-btn.secondary:active { background: #f0f0f0; }
        input[type=file] { display: none; }

        /* ── Required indicator ── */
        .required-hint {
            font-size: 0.8rem; color: #dc2626;
            display: flex; align-items: center; gap: 0.3rem;
            padding: 0 1.1rem 0.85rem;
        }
        .required-hint.satisfied { color: #15803d; }

        /* ── Damage panel ── */
        .damage-panel { border-top: 1px solid #f0f0f0; padding: 0.85rem 1.1rem; }
        .damage-panel-title {
            font-size: 0.8rem; font-weight: 700; color: #888;
            text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.6rem;
        }
        .damage-reasons { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.6rem; }
        .reason-btn {
            padding: 0.35rem 0.65rem; border: 1.5px solid #e0e0e0; border-radius: 20px;
            font-size: 0.8rem; background: white; cursor: pointer; font-family: inherit;
        }
        .reason-btn.selected { border-color: #dc2626; background: #fef2f2; color: #dc2626; font-weight: 600; }
        .damage-custom {
            width: 100%; box-sizing: border-box; padding: 0.6rem 0.75rem;
            border: 1.5px solid #ced4da; border-radius: 8px; font-size: 0.9rem;
            font-family: inherit; resize: none; margin-bottom: 0.5rem;
        }
        .damage-custom:focus { outline: none; border-color: #dc2626; }
        .damage-add-btn {
            display: flex; align-items: center; gap: 0.4rem;
            padding: 0.5rem 0.85rem; background: #dc2626; color: white;
            border: none; border-radius: 8px; font-size: 0.85rem; font-weight: 600;
            cursor: pointer; font-family: inherit;
        }
        .damage-list { margin-top: 0.6rem; }
        .damage-item {
            display: flex; align-items: flex-start; gap: 0.5rem;
            padding: 0.35rem 0; border-bottom: 1px solid #f5f5f5; font-size: 0.85rem;
        }
        .damage-item:last-child { border-bottom: none; }
        .damage-pin {
            background: #dc2626; color: white; width: 18px; height: 18px;
            border-radius: 50%; display: flex; align-items: center; justify-content: center;
            font-size: 0.7rem; font-weight: 700; flex-shrink: 0;
        }

        /* ── Full-screen camera overlay ── */
        .camera-overlay {
            position: fixed; inset: 0; background: #000;
            z-index: 2000; display: flex; flex-direction: column;
        }
        .camera-topbar {
            display: flex; align-items: center; justify-content: space-between;
            padding: 1rem; background: rgba(0,0,0,0.7);
            color: white;
        }
        .camera-topbar-title { font-size: 1rem; font-weight: 600; }
        .camera-close-btn {
            background: rgba(255,255,255,0.2); border: none; color: white;
            padding: 0.5rem 0.85rem; border-radius: 8px; font-size: 0.9rem;
            cursor: pointer; font-family: inherit;
        }
        .camera-viewport {
            flex: 1; display: flex; align-items: center; justify-content: center;
            overflow: hidden; position: relative;
        }
        video {
            width: 100%; height: 100%; object-fit: cover;
        }
        .camera-footer {
            padding: 1.5rem; background: rgba(0,0,0,0.7);
            display: flex; align-items: center; justify-content: center;
        }
        .shutter-btn {
            width: 72px; height: 72px; border-radius: 50%;
            background: white; border: 4px solid rgba(255,255,255,0.6);
            cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .shutter-btn:active { background: #e0e0e0; transform: scale(0.95); }
        .shutter-inner {
            width: 56px; height: 56px; border-radius: 50%; background: white;
            border: 3px solid #1a1a1a;
        }
        .camera-error {
            color: white; text-align: center; padding: 2rem;
            font-size: 0.95rem; line-height: 1.6;
        }
        canvas { display: none; } /* hidden capture canvas */
    `],
    template: `
        <div class="photo-card">
            <div class="photo-header">
                <div class="photo-title">{{ node.props?.title || 'Take a Photo' }}</div>
                <div class="photo-subtitle" *ngIf="node.props?.subtitle">{{ node.props.subtitle }}</div>
            </div>

            <div class="photo-area">
                <!-- Gallery -->
                <div class="thumbnails" *ngIf="photos.length > 0">
                    <div class="thumb-wrap" *ngFor="let p of photos; let i = index">
                        <img [src]="p.dataUrl" [alt]="'Photo ' + (i + 1)"
                             [class.not-stored]="p.uploadState !== 'stored'" />
                        <span class="upload-chip uploading" *ngIf="p.uploadState === 'uploading'">
                            <i class="pi pi-spin pi-spinner"></i>
                        </span>
                        <button class="upload-chip failed" *ngIf="p.uploadState === 'failed'"
                                (click)="retryUpload(i)" title="Not saved — tap to retry">!</button>
                        <button class="thumb-remove" (click)="removePhoto(i)">✕</button>
                        <span class="damage-badge" *ngIf="p.annotations?.length">
                            {{ p.annotations.length }} dmg
                        </span>
                    </div>
                </div>

                <!-- Open camera button -->
                <button class="capture-btn" (click)="openCamera()">
                    <i class="pi pi-camera"></i>
                    {{ photos.length === 0 ? 'Take Photo' : 'Add Another Photo' }}
                </button>

                <!-- Pick an existing photo from the phone (#103) -->
                <button class="capture-btn secondary" (click)="galleryInput.click()">
                    <i class="pi pi-images"></i>
                    Upload from Phone
                </button>

                <!-- Fallback file input (used if getUserMedia fails) -->
                <input #fileInput type="file" accept="image/*" capture="environment"
                    [multiple]="node.props?.multiple ?? false"
                    (change)="onFileSelected($event)" />

                <!-- Gallery input: no capture attribute — that attribute is what makes the phone open the camera -->
                <input #galleryInput type="file" accept="image/*"
                    [multiple]="node.props?.multiple ?? false"
                    (change)="onFileSelected($event)" />
            </div>

            <!-- Required indicator -->
            <!-- Non-blocking by design: a crew with no signal must still be able to finish the move.
                 The warning is loud, the step is not gated on it. -->
            <div class="upload-warning" *ngIf="hasFailedUploads">
                <i class="pi pi-exclamation-triangle"></i>
                <span>
                    {{ failedCount }} photo{{ failedCount === 1 ? '' : 's' }} not saved yet — tap the
                    <strong>!</strong> to retry. You can continue, but unsaved photos will be lost.
                </span>
            </div>

            <div class="required-hint" [class.satisfied]="photos.length > 0"
                *ngIf="node.props?.required">
                <i [class]="photos.length > 0 ? 'pi pi-check-circle' : 'pi pi-exclamation-circle'"></i>
                {{ photos.length > 0 ? 'Photo captured ✓' : 'Photo required to continue' }}
            </div>

            <!-- Damage annotation panel -->
            <div class="damage-panel"
                *ngIf="node.props?.enableDamageAnnotation && photos.length > 0">
                <div class="damage-panel-title">
                    <i class="pi pi-flag-fill" style="color:#dc2626;margin-right:0.3rem;"></i>
                    Mark Damage (optional)
                </div>
                <div class="damage-reasons">
                    <button class="reason-btn" *ngFor="let r of damageReasons"
                        [class.selected]="selectedReason === r"
                        (click)="selectReason(r)">{{ r }}
                    </button>
                </div>
                <textarea *ngIf="selectedReason === 'Other'"
                    class="damage-custom" rows="2"
                    placeholder="Describe the damage..."
                    [(ngModel)]="customReason">
                </textarea>
                <button class="damage-add-btn" *ngIf="selectedReason"
                    (click)="addAnnotation()">
                    <i class="pi pi-plus"></i> Add to Most Recent Photo
                </button>
                <div class="damage-list" *ngIf="latestAnnotations.length > 0">
                    <div class="damage-item" *ngFor="let a of latestAnnotations; let i = index">
                        <span class="damage-pin">{{ i + 1 }}</span>
                        <span>{{ a.reason === 'Other' ? a.custom : a.reason }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Full-screen camera overlay -->
        <div class="camera-overlay" *ngIf="cameraOpen">
            <div class="camera-topbar">
                <span class="camera-topbar-title">{{ node.props?.title || 'Take a Photo' }}</span>
                <button class="camera-close-btn" (click)="closeCamera()">Cancel</button>
            </div>
            <div class="camera-viewport">
                <video #videoEl autoplay playsinline muted></video>
                <div class="camera-error" *ngIf="cameraError">
                    {{ cameraError }}
                    <br><br>
                    <button class="capture-btn" style="max-width:200px;margin:0 auto;"
                        (click)="useFallback()">
                        <i class="pi pi-upload"></i> Choose from Library
                    </button>
                </div>
            </div>
            <div class="camera-footer" *ngIf="!cameraError">
                <button class="shutter-btn" (click)="captureFromCamera()">
                    <div class="shutter-inner"></div>
                </button>
            </div>
        </div>

        <!-- Hidden canvas for frame capture -->
        <canvas #captureCanvas></canvas>
    `,
})
export class NavPhotoCaptureComponent extends NavBaseComponent implements OnDestroy {
    @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
    @ViewChild('captureCanvas') captureCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    /**
     * `dataUrl` is a **preview only** and is never persisted.
     *
     * A phone photo is 1-3MB as base64, and `navigatorData` is a JSON string rewritten on every step
     * advance and every inventory patch — putting images in it would make every navigator write
     * megabytes, on the same field that #48's clobbers already contend over. Only `artifactId` and
     * the annotations are stored.
     */
    photos: Array<{
        dataUrl: string;
        annotations: Array<{ reason: string; custom: string }>;
        artifactId?: string;
        uploadState: 'uploading' | 'stored' | 'failed';
    }> = [];

    private readonly store = inject(Store);
    private readonly artifactService = inject(ArtifactService);
    private readonly route = inject(ActivatedRoute);

    cameraOpen = false;
    cameraError = '';
    private stream: MediaStream | null = null;

    damageReasons = [
        'Pre-existing damage', 'Scratch', 'Dent', 'Break/Crack',
        'Stain', 'Water damage', 'Missing piece', 'Other',
    ];
    selectedReason = '';
    customReason = '';

    get latestAnnotations() {
        return this.photos[this.photos.length - 1]?.annotations ?? [];
    }

    get isSatisfied(): boolean {
        if (!this.node.props?.required) return true;
        return this.photos.length > 0;
    }

    async openCamera() {
        this.cameraOpen = true;
        this.cameraError = '';

        if (!navigator.mediaDevices?.getUserMedia) {
            this.cameraError = 'Camera not available on this device. Use the button below to choose a photo.';
            return;
        }

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
                audio: false,
            });
            // Wait for next tick so *ngIf renders the video element
            setTimeout(() => {
                if (this.videoEl?.nativeElement && this.stream) {
                    this.videoEl.nativeElement.srcObject = this.stream;
                }
            }, 0);
        } catch (err: any) {
            this.cameraError = `Could not access camera: ${err?.message ?? err}. Use the button below instead.`;
        }
    }

    closeCamera() {
        this.stopStream();
        this.cameraOpen = false;
    }

    useFallback() {
        this.closeCamera();
        this.fileInput.nativeElement.click();
    }

    captureFromCamera() {
        const video = this.videoEl?.nativeElement;
        const canvas = this.captureCanvas?.nativeElement;
        if (!video || !canvas) return;

        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;

        // Compress: max 1920px on longest edge
        const MAX = 1920;
        let sw = w, sh = h;
        if (sw > MAX || sh > MAX) {
            const r = Math.min(MAX / sw, MAX / sh);
            sw = Math.round(sw * r);
            sh = Math.round(sh * r);
        }
        canvas.width = sw;
        canvas.height = sh;
        canvas.getContext('2d')!.drawImage(video, 0, 0, sw, sh);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.80);

        this._addPhoto(dataUrl);
        this.closeCamera();
    }

    onFileSelected(e: Event) {
        const input = e.target as HTMLInputElement;
        Array.from(input.files ?? []).forEach(file => this.compressAndAdd(file));
        input.value = '';
    }

    private compressAndAdd(file: File) {
        const reader = new FileReader();
        reader.onload = ev => {
            const img = new Image();
            img.onload = () => {
                const MAX = 1920;
                let { width: w, height: h } = img;
                if (w > MAX || h > MAX) {
                    const r = Math.min(MAX / w, MAX / h);
                    w = Math.round(w * r); h = Math.round(h * r);
                }
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.80);
                this._addPhoto(dataUrl);
            };
            img.src = ev.target!.result as string;
        };
        reader.readAsDataURL(file);
    }

    /**
     * Add a captured photo and start storing it immediately.
     *
     * Uploading on capture rather than on step advance is what makes the photo survive a step change
     * and a reload — #43's complaint was that photos lived in this component field and died with it.
     * It also keeps the image out of `navigatorData`; only the resulting artifact id goes in.
     */
    private _addPhoto(dataUrl: string) {
        const photo = { dataUrl, annotations: [], uploadState: 'uploading' as const };
        this.photos.push(photo);
        this._notifyReady();
        this._upload(photo);
    }

    private _upload(photo: (typeof this.photos)[number]) {
        uploadCapture(this.artifactService, photo.dataUrl, `navigator-photo-${this.photos.length}.jpg`)
            .subscribe((artifactId) => {
                photo.artifactId = artifactId ?? undefined;
                photo.uploadState = artifactId ? 'stored' : 'failed';
                if (artifactId) { this._persistArtifactIds(); }
                this._notifyReady();
            });
    }

    retryUpload(i: number) {
        const photo = this.photos[i];
        if (!photo || photo.uploadState === 'uploading') { return; }
        photo.uploadState = 'uploading';
        this._upload(photo);
    }

    /** True when at least one photo did not reach storage. Surfaced as a warning, never a block. */
    get hasFailedUploads(): boolean {
        return this.photos.some(p => p.uploadState === 'failed');
    }

    get uploadingCount(): number {
        return this.photos.filter(p => p.uploadState === 'uploading').length;
    }

    get failedCount(): number {
        return this.photos.filter(p => p.uploadState === 'failed').length;
    }

    /**
     * Write the stored artifact ids and annotations into `navigatorData` under this step's key.
     *
     * `stepKey` has been documented as the storage key since the component was written and was read
     * by nothing (#43). This is what finally uses it. Data-only patch — deliberately not a step
     * write, so it cannot move the crew (#46).
     */
    private _persistArtifactIds() {
        persistStepCapture(
            { store: this.store, route: this.route },
            this.node.props?.stepKey ?? '',
            {
                kind: 'photo',
                artifactIds: this.photos.map(p => p.artifactId).filter((id): id is string => !!id),
                annotations: this.photos.flatMap(p => p.annotations),
            },
        );
    }

    removePhoto(i: number) {
        this.photos.splice(i, 1);
        this._persistArtifactIds();
        this._notifyReady();
    }

    selectReason(r: string) {
        this.selectedReason = this.selectedReason === r ? '' : r;
        if (r !== 'Other') this.customReason = '';
    }

    addAnnotation() {
        if (!this.selectedReason || this.photos.length === 0) return;
        this.photos[this.photos.length - 1].annotations.push({
            reason: this.selectedReason,
            custom: this.customReason,
        });
        this.selectedReason = '';
        this.customReason = '';
    }

    private stopStream() {
        this.stream?.getTracks().forEach(t => t.stop());
        this.stream = null;
    }

    private _notifyReady() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.isSatisfied);
        }
    }

    ngOnDestroy() { this.stopStream(); }
}
